import { ReactNode } from "react";
import AnimatedGradientBackground from "./AnimatedGradientBackground";

interface PageHeroProps {
  title: string;
  subtitle?: string;
  description?: string;
  children?: ReactNode;
  backgroundImage?: string;
  variant?: "default" | "vibrant";
}

const PageHero = ({ 
  title, 
  subtitle, 
  description, 
  children, 
  backgroundImage,
  variant = "vibrant"
}: PageHeroProps) => {
  return (
    <section 
      className="relative min-h-[40vh] md:min-h-[50vh] flex items-center justify-center overflow-hidden"
      style={backgroundImage ? { backgroundImage: `url(${backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
    >
      {/* Animated gradient background */}
      {variant === "vibrant" && !backgroundImage && (
        <AnimatedGradientBackground variant="vibrant" />
      )}
      
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/70 to-background" />
      
      {/* Floating particles */}
      {variant === "vibrant" && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full bg-eco-green/30"
              style={{
                left: `${15 + i * 15}%`,
                top: `${20 + (i % 3) * 25}%`,
                animation: `float ${3 + i * 0.5}s ease-in-out infinite`,
                animationDelay: `${i * 0.3}s`,
              }}
            />
          ))}
          {[...Array(4)].map((_, i) => (
            <div
              key={`blue-${i}`}
              className="absolute w-3 h-3 rounded-full bg-sky-blue/20"
              style={{
                right: `${10 + i * 20}%`,
                bottom: `${30 + (i % 2) * 20}%`,
                animation: `float ${4 + i * 0.5}s ease-in-out infinite`,
                animationDelay: `${i * 0.5}s`,
              }}
            />
          ))}
        </div>
      )}
      
      {/* Content */}
      <div className="container mx-auto px-6 relative z-10 text-center py-12 md:py-20">
        {subtitle && (
          <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-eco-green/20 via-primary/20 to-sky-blue/20 text-primary text-sm font-semibold mb-6 animate-fade-in border border-primary/20">
            {subtitle}
          </span>
        )}
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 animate-fade-in">
          {title}
        </h1>
        {description && (
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-8 animate-fade-in">
            {description}
          </p>
        )}
        {children}
      </div>
    </section>
  );
};

export default PageHero;
